import { finalPdfPollMs } from "@shared/final-pdf.ts";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { DashboardNav } from "../components/DashboardNav.tsx";
import { asideDrawerOpen, setAsideDrawerOpen } from "../components/legacy/app-state.tsx";
import { UserAvatar } from "../components/UserAvatar.tsx";
import { LeftArrowIcon, RightArrowIcon } from "../icons/index.tsx";
import { type AppConfig, api, type Campaign, type Profile } from "../lib/api.ts";

export const Route = createFileRoute("/account")({
  component: AccountLayout,
  beforeLoad: async ({ context }) => {
    const config = await context.queryClient.ensureQueryData({
      queryKey: ["config"],
      queryFn: () => api.get<AppConfig>("/config"),
    });
    if (!config.user) throw redirect({ to: "/sign-in" });
  },
});

export function useProfile() {
  return useQuery({
    queryKey: ["profile"],
    queryFn: () => api.get<{ profile: Profile }>("/account/profile"),
  });
}

const ICON_BTN =
  "relative flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900";

function AccountLayout() {
  const { data } = useProfile();
  const navigate = useNavigate();
  const campaigns = useQuery({
    queryKey: ["campaigns", "all"],
    queryFn: () => api.get<{ campaigns: Campaign[] }>("/campaigns?includeDeleted=1"),
    refetchInterval: (query) => finalPdfPollMs(query.state.data?.campaigns),
  });
  const profile = data?.profile;
  const customerId = profile?.id ?? "";
  const [open, setOpen] = useState(asideDrawerOpen);
  const setDrawer = (next: boolean) => {
    setOpen(next);
    setAsideDrawerOpen(next);
  };

  return (
    <div className="flex min-h-[calc(100dvh-var(--header-h))] bg-gray-50">
      {/* A div, not an aside: legacy.css parks every `aside` off-canvas.
          Below lg the spacer stays rail-width and the open panel overlays the
          page; from lg up the panel sits in flow and sticks under the header. */}
      <div className={`relative shrink-0 ${open ? "w-16 lg:w-72" : "w-16"}`}>
        <div
          className={`absolute inset-y-0 left-0 z-20 flex flex-col border-r border-gray-200 bg-white shadow-lg transition-[width] duration-300 lg:sticky lg:top-[var(--header-h)] lg:h-[calc(100vh-var(--header-h))] lg:shadow-none ${
            open ? "w-72" : "w-16"
          }`}
        >
          <div className={`flex-1 overflow-y-auto ${open ? "px-4 pb-4" : "px-3 pb-3"}`}>
            <div className={`flex items-start gap-1 pt-2 ${open ? "" : "justify-center"}`}>
              {open && (
                <div className="flex min-w-0 flex-1 items-center gap-2.5 px-1 py-1">
                  <UserAvatar
                    name={profile?.name}
                    image={profile?.image}
                    updatedAt={profile?.updatedAt}
                    role={profile?.role}
                    size="md"
                    decorative
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {profile?.name ?? "\u00a0"}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {profile?.organisation ?? "Account"}
                    </p>
                  </div>
                </div>
              )}
              <button
                type="button"
                className={`${ICON_BTN} shrink-0 ${open ? "ml-auto" : ""}`}
                aria-label={open ? "Collapse panel" : "Expand panel"}
                aria-expanded={open}
                title={open ? "Collapse panel" : "Expand panel"}
                onClick={() => setDrawer(!open)}
              >
                {open ? (
                  <LeftArrowIcon className="h-5 w-5" />
                ) : (
                  <RightArrowIcon className="h-5 w-5" />
                )}
              </button>
            </div>

            {!open && (
              <button
                type="button"
                className="mt-2 flex w-full justify-center"
                title={profile?.name ?? "Account"}
                aria-label="Expand panel"
                onClick={() => setDrawer(true)}
              >
                <UserAvatar
                  name={profile?.name}
                  image={profile?.image}
                  updatedAt={profile?.updatedAt}
                  role={profile?.role}
                  size="md"
                  decorative
                />
              </button>
            )}

            <DashboardNav
              open={open}
              customerId={customerId}
              role={profile?.role}
              campaigns={campaigns.data?.campaigns ?? []}
              onOpenCampaign={(c) =>
                navigate({
                  to: "/app/$customerId/mycampaigns/$campaignId",
                  params: {
                    customerId: c.customer_id ?? c.user_id ?? customerId,
                    campaignId: c.id,
                  },
                })
              }
              onAllCampaigns={() =>
                navigate({ to: "/app/$customerId/mycampaigns", params: { customerId } })
              }
              onExpand={() => setDrawer(true)}
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col px-4 py-6 sm:px-6 lg:px-8">
        <Outlet />
      </div>
    </div>
  );
}
